/**
 * Events hub constants (2026-10).
 *
 * Ids mirror the seeded lookup tables in ccms_eventmanagement (CCMSLib.Models.Events.EventHubModels).
 * The lookup tables stay the source of truth for names (GET api/events/lookups); these constants
 * only let code compare ids without a round-trip.
 */
/** EventSource lookup ids. */
export const EventSourceIds = {
    Ccms: 1,
    PlanningCenterCalendar: 2,
    Ics: 3,
};
/** EventReviewStatus lookup ids. */
export const EventReviewStatusIds = {
    Ready: 1,
    NeedsReview: 2,
};
/** EventSyncRunStatus lookup ids. */
export const EventSyncRunStatusIds = {
    Running: 1,
    Succeeded: 2,
    Failed: 3,
    /** Completed but deletions were skipped (incomplete read or too many missing). */
    Partial: 4,
};
/** EventSourceMapKind lookup ids (what a source mapping maps). */
export const EventSourceMapKindIds = {
    LocationText: 1,
    Tag: 2,
    Room: 3,
};
/** EventSourceMappingStatus lookup ids. */
export const EventSourceMappingStatusIds = {
    Unmapped: 1,
    Mapped: 2,
    Ignored: 3,
    /** Events carrying this value are not imported at all. */
    ExcludeEvents: 4,
};
/** EventType id seeded for synced events whose tags matched no type ("Needs a type"). */
export const EVENT_TYPE_NEEDS_A_TYPE_ID = 100;
/** AspNetRights checked by CCMS.Api.EventManagement (seeded by PG identity/004). */
export const EVENT_RIGHTS = {
    View: 'Event.View',
    Create: 'Event.Create',
    Update: 'Event.Update',
    Delete: 'Event.Delete',
    /** Resolve events held for review. */
    Review: 'Event.Review',
    TypeView: 'Event.Type.View',
    TypeCreate: 'Event.Type.Create',
    TypeUpdate: 'Event.Type.Update',
    TypeDelete: 'Event.Type.Delete',
    SourceView: 'Event.Source.View',
    SourceManage: 'Event.Source.Manage',
    /** Test the connection and start a sync. */
    SourceSync: 'Event.Source.Sync',
    MappingManage: 'Event.Mapping.Manage',
    // Stage 1B (PG identity/005)
    /** Add, approve, dismiss and remove an event's links (the event's coordinator may too). */
    LinkManage: 'Event.Link.Manage',
    /** Create and edit manual CCMS tags and set them on events. */
    TagManage: 'Event.Tag.Manage',
    /** Create, edit, preview and re-run link rules. */
    RuleManage: 'Event.Rule.Manage',
    /** Review learned rule proposals and the learning settings. */
    LearningReview: 'Event.Learning.Review',
    // Stage 2 (PG identity/006)
    /** Create, edit and delete playbooks (and draft plans from them). */
    PlaybookManage: 'Event.Playbook.Manage',
    /** Approve a plan, which creates its work in other apps as the approver. The event's coordinator may too. */
    PlanApprove: 'Event.Plan.Approve',
    // Stage 3 (PG identity/007)
    /** Ask AI for an event's link and playbook suggestions (Admin passes too; being the coordinator is not enough). */
    AiUse: 'Event.AI.Use',
};
/** Roles that open the Events module. Admin passes every Event endpoint. */
export const EVENT_ROLES = {
    Admin: 'Admin',
    /** All Event.* rights. */
    EventsAdmin: 'EventsAdmin',
    /** Read rights: Event.View, Event.Type.View and Event.Source.View. */
    EventsAccess: 'Events Access',
    /** Event.View and Event.Link.Manage (PG identity/005). */
    EventsCoordinator: 'Events Coordinator',
};
// ---------------------------------------------------------------- Stage 1B: links, tags, rules, learning
/** EventLinkType lookup ids (what an event link points at). */
export const EventLinkTypeIds = {
    Department: 1,
    Designation: 2,
    Ministry: 3,
    MinistryTeam: 4,
    Venue: 5,
    Location: 6,
    /** TargetKey is the user id. */
    Person: 7,
    BookableResource: 8,
    Asset: 9,
    Task: 20,
    Project: 21,
    Meeting: 22,
    Booking: 23,
    Budget: 24,
    PurchaseRequest: 25,
    Announcement: 26,
    Campaign: 27,
    Form: 28,
};
/** EventLinkRole lookup ids. */
export const EventLinkRoleIds = {
    Involved: 1,
    Lead: 2,
    Supporting: 3,
    Related: 4,
};
/** EventLinkOrigin lookup ids (what produced a link). */
export const EventLinkOriginIds = {
    Manual: 1,
    Rule: 2,
    LearnedRule: 3,
    Ai: 4,
    Playbook: 5,
    SourceTag: 6,
};
/** EventLinkStatus lookup ids. */
export const EventLinkStatusIds = {
    Suggested: 1,
    Approved: 2,
    Dismissed: 3,
    Removed: 4,
};
/** EventSignalType lookup ids (what a rule condition looks at). */
export const EventSignalTypeIds = {
    Tag: 1,
    TagGroup: 2,
    EventType: 3,
    Campus: 4,
    Venue: 5,
    Source: 6,
    TitleKeyword: 7,
    IsPublic: 8,
    DayOfWeek: 9,
    TimeBand: 10,
    AllCampuses: 11,
};
/** EventRuleOperator lookup ids. */
export const EventRuleOperatorIds = {
    EqualsTo: 1,
    /** Value is a comma separated list. */
    In: 2,
    Contains: 3,
    NotEquals: 4,
};
/** EventRuleActionType lookup ids. */
export const EventRuleActionTypeIds = {
    AddLink: 1,
    SetCoordinator: 2,
    /** Stage 2: creates a draft plan from a playbook (targetKey = playbook id) once per event and playbook. */
    SuggestPlaybook: 3,
};
/** EventRuleCandidateStatus lookup ids (learned rule proposals). */
export const EventRuleCandidateStatusIds = {
    Proposed: 1,
    Accepted: 2,
    Rejected: 3,
    Snoozed: 4,
};
// ---------------------------------------------------------------- Stage 2: playbooks and plans
/** EventPlaybookItemType lookup ids (what a playbook item creates). */
export const EventPlaybookItemTypeIds = {
    Task: 1,
    /** Instantiates a ProjectManagement template; the project then creates its own tasks. */
    ProjectFromTemplate: 2,
    Meeting: 3,
    Booking: 4,
    /** Stage 3: a budget made from the plan's project (needs a ProjectFromTemplate item in the same plan). */
    BudgetFromProject: 5,
    /** Stage 3: a draft purchase request, linked to the plan's project so spend routes to its budget. */
    PurchaseRequest: 6,
    /** Stage 3: an announcement linked to the event, active from the offset until the event ends. */
    Announcement: 7,
};
/** EventPlanAnchor lookup ids (what a playbook item's offset counts from). */
export const EventPlanAnchorIds = {
    EventStart: 1,
    EventEnd: 2,
};
/** EventAssigneeStrategy lookup ids (who a generated item is assigned to). */
export const EventAssigneeStrategyIds = {
    /** Every member of a designation (TaskManagement assigns all members). */
    Designation: 1,
    /** The event's linked department with role Lead (else any linked department). */
    LeadDepartment: 2,
    Coordinator: 3,
    SpecificUser: 4,
};
/** EventPlanStatus lookup ids. */
export const EventPlanStatusIds = {
    Draft: 1,
    Approved: 2,
    Executing: 3,
    Completed: 4,
    Partial: 5,
    Cancelled: 6,
    /** The event moved after the plan was approved; generated work is NOT moved automatically. */
    OutOfDate: 7,
};
/** EventPlanItemStatus lookup ids. */
export const EventPlanItemStatusIds = {
    Proposed: 1,
    Queued: 2,
    Creating: 3,
    Created: 4,
    Failed: 5,
    Skipped: 6,
};
//# sourceMappingURL=EventConstants.js.map